<template>
  <UmisisContainer class="pb-10">
    <BusinessSystemCreateModal ref="businessSystemCreateModal" @created="handleBusinessSystemCreated" />
    <div class="flex items-center justify-between py-10">
      <h1 class="text-3xl font-bold tracking-tight">
        Business Systems
      </h1>
      <div class="flex gap-4">
        <Input v-model="searchQuery" placeholder="Search systems..." class="w-64" />
        <Button @click="openBusinessSystemCreateModal">
          New System
        </Button>
      </div>
    </div>


    <div class="mb-6 overflow-x-auto">
      <div class="flex min-w-max gap-2 pb-2">
        <Button :variant="selectedCategoryId === null ? 'default' : 'outline'" @click="selectCategory(null)">
          All
        </Button>
        <Button v-for="category in categories" :key="category.id" :variant="isSelectedCategory(category.id) ? 'default' : 'outline'" @click="selectCategory(category.id)">
          {{ category.name }}
        </Button>
      </div>
    </div>

    <UmisisAnimatedList :items="filteredSystems" list-class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" item-class="min-w-0" :stagger-ms="70" :max-delay-ms="350">
      <template #default="{ item: system }">
        <BusinessSystemCard
          :system="system"
          @select-category="selectCategory"
        />
      </template>
    </UmisisAnimatedList>

    <div v-if="(businessSystemStore.total ?? 0) > 0" class="mt-8">
      <Pagination
        v-model:page="businessSystemStore.page"
        :items-per-page="businessSystemStore.perPage"
        :total="businessSystemStore.total ?? 0"
      />
    </div>
  </UmisisContainer>
</template>

<script setup>
import UmisisAnimatedList from '../../../../../../resources/js/components/umisis/generic/UmisisAnimatedList.vue'
import UmisisContainer from '../../../../../../resources/js/components/umisis/generic/UmisisContainer.vue'
import BusinessSystemCard from '../components/business-systems/BusinessSystemCard.vue'
import { Input } from '../../../../../../resources/js/components/ui/input/index.js'
import { Button } from '../../../../../../resources/js/components/ui/button/index.js'
import { Pagination } from '../../../../../../resources/js/components/ui/pagination/index.js'
import { useBusinessSystemStore } from '../stores/businessSystems.js'
import { useBusinessSystemCategoryStore } from '../stores/businessSystemCategories.js'
import { computed, onMounted, ref, watch } from 'vue'
import BusinessSystemCreateModal from '../components/business-systems/BusinessSystemCreateModal.vue'
import { useDebounceFn } from '@vueuse/core'

const businessSystemCreateModal = ref(null)
const businessSystemStore = useBusinessSystemStore()
const businessSystemCategoryStore = useBusinessSystemCategoryStore()
const selectedCategoryId = ref(null)
const searchQuery = ref('')

const fetchBusinessSystems = async () => {
  await businessSystemStore.fetchItems()
}

const debouncedFetchBusinessSystems = useDebounceFn(async (query) => {
  const previousPage = businessSystemStore.page

  businessSystemStore.searchChanged(query)

  if (businessSystemStore.page === previousPage) {
    await fetchBusinessSystems()
  }
}, 300)

watch(searchQuery, (value) => {
  const normalizedQuery = value.trim() === '' ? null : value.trim()
  debouncedFetchBusinessSystems(normalizedQuery)
})

watch(() => businessSystemStore.page, async (newPage, previousPage) => {
  if (newPage === previousPage) {
    return
  }

  await fetchBusinessSystems()
})

onMounted(() => {
  fetchBusinessSystems()
  businessSystemCategoryStore.fetchItems()
})

// make systems a computed property that returns the items from the store
const systems = computed(() => businessSystemStore.items)
const categories = computed(() => businessSystemCategoryStore.items)
const filteredSystems = computed(() => {
  if (selectedCategoryId.value === null) {
    return systems.value
  }

  return systems.value.filter((system) => Number(system.business_system_category_id) === Number(selectedCategoryId.value))
})

const selectCategory = (categoryId) => {
  selectedCategoryId.value = categoryId
}

const isSelectedCategory = (categoryId) => {
  return selectedCategoryId.value !== null && Number(selectedCategoryId.value) === Number(categoryId)
}

const openBusinessSystemCreateModal = () => {
  businessSystemCreateModal.value?.openModal()
}

const handleBusinessSystemCreated = async () => {
  await fetchBusinessSystems()
}

</script>
